/**
 * Dashboard hooks
 *
 * One server call per section: `GET /librarian/dashboard-overview/` already
 * aggregates statistics, activity, tasks and notifications (cached per section on
 * the backend), so nothing here fans out to the catalog/inventory/loans/users
 * statistics endpoints to rebuild the same numbers client-side.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { dashboardApi } from '@/services/dashboard-api';

export const dashboardQueryKeys = {
  all: ['dashboard'] as const,
  overview: () => [...dashboardQueryKeys.all, 'overview'] as const,
  weeklyActivity: () => [...dashboardQueryKeys.all, 'weekly-activity'] as const,
};

// Re-export types from dashboard API for convenience
export type {
  DashboardStatistics,
  ActivityItem,
  DashboardTask,
  DashboardNotification,
  QuickStat,
  DashboardOverviewResponse,
  WeeklyActivityItem,
} from '@/services/dashboard-api';

/**
 * Consolidated dashboard overview.
 */
export const useDashboardOverview = () => {
  return useQuery({
    queryKey: dashboardQueryKeys.overview(),
    queryFn: () => dashboardApi.getDashboardOverview(),
    staleTime: 30 * 1000,
    refetchInterval: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

/**
 * Statistics section of the overview (same query, no extra request).
 */
export const useDashboardStatistics = () => {
  const query = useDashboardOverview();
  return { ...query, data: query.data?.statistics };
};

/**
 * Quick-stat tiles as computed by the backend.
 */
export const useQuickStats = () => {
  const { data } = useDashboardOverview();
  return data?.quick_stats ?? [];
};

/**
 * Issues/returns for the last 7 days.
 */
export const useWeeklyActivity = () => {
  return useQuery({
    queryKey: dashboardQueryKeys.weeklyActivity(),
    queryFn: () => dashboardApi.getWeeklyActivity(),
    staleTime: 2 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
};

/**
 * Invalidation helpers for after a desk operation (issue, return, add book).
 */
export const useRealTimeDashboard = () => {
  const queryClient = useQueryClient();

  const refreshDashboard = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: dashboardQueryKeys.all });
  }, [queryClient]);

  const onBookManagementOperation = useCallback(
    (_operationType: 'create' | 'issue' | 'return' | 'bulk') => {
      refreshDashboard();
      queryClient.invalidateQueries({ queryKey: ['catalog', 'statistics'] });
      queryClient.invalidateQueries({ queryKey: ['inventory', 'statistics'] });
      queryClient.invalidateQueries({ queryKey: ['loans', 'statistics'] });
    },
    [queryClient, refreshDashboard]
  );

  return {
    refreshDashboard,
    refreshStatistics: refreshDashboard,
    refreshBookManagement: refreshDashboard,
    onBookManagementOperation,
  };
};
